import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Share2, X } from 'lucide-react'
import { toast } from 'sonner'
import { lcCoachingApi } from '@/api/lcCoaching'
import { GrowthAlertContent } from '@/components/growth-alert/GrowthAlertReport'

export default function GrowthAlertPublicPage() {
  const { token = '' } = useParams()
  const { t } = useTranslation('coaching')
  const [viewerPhotoUrl, setViewerPhotoUrl] = useState<string | null>(null)
  const query = useQuery({
    queryKey: ['growth-alert', 'public', token],
    queryFn: () => lcCoachingApi.getPublicGrowthAlert(token),
    enabled: token.length > 0,
    retry: false,
  })

  if (query.isLoading) return <StatusPage message={t('growthAlert.loading')} />
  if (query.isError) return <StatusPage message={t('growthAlert.publicNotFound')} />
  if (query.data?.expired) return <StatusPage message={t('growthAlert.publicExpired')} />

  const preview = query.data?.preview

  const shareCurrentPage = async () => {
    const shareData = {
      title: t('growthAlert.title'),
      url: window.location.href,
    }

    try {
      if (navigator.share) {
        await navigator.share(shareData)
      } else {
        await navigator.clipboard.writeText(shareData.url)
        toast.success(t('growthAlert.publicShareCopied'))
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return
      toast.error(t('growthAlert.publicShareError'))
    }
  }

  return (
    <main className="min-h-screen bg-[#f7f7f7] pb-16 md:pb-20">
      <header className="flex h-16 items-center justify-between border-b border-black/10 bg-white px-5 md:h-[72px] md:px-10">
        <div className="flex min-w-0 items-center gap-2 md:gap-3">
          <img
            src="/report/diagnostic/logo_kor.svg"
            width={80}
            height={16}
            alt={t('growthAlert.brandAlt')}
            className="h-4 w-20 shrink-0"
          />
          {preview?.organizationName ? (
            <>
              <span className="h-3 w-px shrink-0 bg-[#d9d9d9] md:h-4" aria-hidden="true" />
              <span className="truncate text-xs font-bold text-[#888] md:text-sm">
                {preview.organizationName}
              </span>
            </>
          ) : null}
        </div>
        <button
          type="button"
          onClick={() => void shareCurrentPage()}
          aria-label={t('growthAlert.publicShare')}
          className="hidden size-12 items-center justify-center rounded-full text-[#555] transition-colors hover:bg-[#f4f4f4] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#4599ff] md:flex"
        >
          <Share2 className="size-6" aria-hidden="true" />
        </button>
      </header>

      <article className="mx-auto w-full max-w-[960px] md:mt-6">
        <GrowthAlertContent
          preview={preview}
          isLoading={false}
          isError={false}
          t={t}
          onOpenPhoto={setViewerPhotoUrl}
          surface="public"
          onRefreshVideoUrl={async (id) => {
            const result = await query.refetch()
            if (result.data?.expired) return undefined
            return result.data?.preview?.lessons.flatMap(lesson => lesson.explain.videos ?? []).find(video => video.id === id)?.playbackUrl ?? undefined
          }}
        />
      </article>

      {viewerPhotoUrl ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={t('growthAlert.imageViewer')}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          onClick={() => setViewerPhotoUrl(null)}
        >
          <button
            type="button"
            onClick={() => setViewerPhotoUrl(null)}
            aria-label={t('growthAlert.close')}
            className="absolute right-4 top-4 flex size-10 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur-sm"
          >
            <X className="size-6" aria-hidden="true" />
          </button>
          <img
            src={viewerPhotoUrl}
            alt={t('growthAlert.imageViewer')}
            className="max-h-full max-w-full rounded-xl object-contain"
            onClick={(event) => event.stopPropagation()}
          />
        </div>
      ) : null}
    </main>
  )
}

function StatusPage({ message }: { message: string }) {
  const { t } = useTranslation('coaching')
  return (
    <main className="flex min-h-screen items-center justify-center bg-[#f7f7f7] p-6">
      <div className="w-full max-w-md rounded-2xl bg-white p-8 text-center shadow-[0_6px_18px_rgba(0,0,0,0.06)]">
        <img
          src="/report/diagnostic/logo_kor.svg"
          width={119}
          height={24}
          alt={t('growthAlert.brandAlt')}
          className="mx-auto h-6 w-[119px]"
        />
        <p className="mt-6 text-[#555]">{message}</p>
      </div>
    </main>
  )
}
